import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { COUNTRIES_WITH_INTL_DOMAIN } from '../../../../utils/constants';
import { changeCurrencyCode } from '../../../../utils/currency';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { countryToFlag } from '../../../../utils/location';
import { ShippingCountryState, changeCountry } from '../../../../utils/shipping';
import AloDropdown, { DropdownItem } from '../YogaUI/alo-ui/AloDropdown';
import { toJS } from 'mobx';
import { isSiteInEnglish } from 'src/scripts/utils/helpers';
import { updateLanguageCode } from 'src/scripts/utils/country-language';

let ShippingCountryDropdown = () => {
  const supportedCountries = toJS(ShippingCountryState.supportedCountries);
  const dropdownOptions = useMemo(() => {
    return supportedCountries?.map(({ countryCode, countryName, currency: { code } }) => ({
      key: countryCode,
      value: countryCode,
      flag: countryCode.toLowerCase(),
      text: countryName,
      currency: code,
    }));
  }, [supportedCountries]);

  const dropdownItems = dropdownOptions?.map(({ key, text }) => ({
    key: key.toLowerCase(),
    text,
    flag: key.toLowerCase(),
  }));

  const dropdownValue = {
    key: ShippingCountryState?.countryCode?.toLowerCase() || '',
    text: ShippingCountryState?.country?.name || '',
    flag: ShippingCountryState?.countryCode?.toLowerCase() || '',
  };

  // ? [REVISIT] if this uses a viewModel vs updating the state object directly (onChange) simply the logic considerably
  // ? - https://github.com/mobxjs/mobx-utils?tab=readme-ov-file#createviewmodel
  const onDropdownChange = (key: string) => {
    const currency = supportedCountries?.find((country) => country.countryCode === key.toUpperCase())?.currency.code;
    const countryCode = supportedCountries?.find((country) => country.countryCode === key.toUpperCase())?.countryCode;
    const defaultLanguageCode = supportedCountries
      ?.find((country) => country.countryCode?.toUpperCase() === key.toUpperCase())
      ?.languages?.[0]?.locale?.toLowerCase();

    const shouldUpdateUserPreference = !COUNTRIES_WITH_INTL_DOMAIN?.some(
      (country) => country.countryCode === countryCode
    );

    clearCurrencyCountryParams();
    changeCountry(countryCode, { updateUserPreference: shouldUpdateUserPreference });
    changeCurrencyCode(currency, { updateUserPreference: shouldUpdateUserPreference });
    updateLanguageCode(defaultLanguageCode);
  };

  const renderOption = (option: DropdownItem) => (
    <>
      {option?.flag ? <span className="GlobalLinkNoTx flag">{countryToFlag(option.flag)}</span> : null}
      <span style={{ marginLeft: '1rem' }} className="GlobalLinkNoTx">
        {option.text}
      </span>
    </>
  );

  const flagElement = ShippingCountryState.countryCode ? (
    <span className="flag">{countryToFlag(ShippingCountryState.countryCode)}</span>
  ) : null;

  if (!dropdownItems) return null;

  return (
    <div className={`sc-dropdown`}>
      <h6 className="title h6">Shipping Location</h6>
      <AloDropdown
        searchable
        inputReadOnly={!isSiteInEnglish()}
        searchableDropdownPrependIcon={flagElement}
        dropdownItems={dropdownItems}
        // ? can we swap this from onChange to onSubmit?
        onChange={({ key }) => onDropdownChange(key)}
        placeholder="Select Country"
        value={dropdownValue}
        MUIAutocompleteProps={{
          disableClearable: true,
          // disablePortal is false by default but is important
          // because parent Modal touch pan is disabled to fix ios scrolling
          disablePortal: false,
          renderOption,
        }}
      />
    </div>
  );
};

ShippingCountryDropdown = observer(ShippingCountryDropdown);
export { ShippingCountryDropdown };

export const clearCurrencyCountryParams = () => {
  // Clear Country and currency params
  if (
    InitialUrlSearchParams.get('setCurrency') ||
    InitialUrlSearchParams.get('setCountry') ||
    InitialUrlSearchParams.get('glCurrency') ||
    InitialUrlSearchParams.get('glCountry') ||
    InitialUrlSearchParams.get('country')
  ) {
    const urlWithoutParams = window.location.href.split('?')[0];
    window.history.replaceState({}, document.title, urlWithoutParams);
    InitialUrlSearchParams.delete('setCountry');
    InitialUrlSearchParams.delete('setCurrency');
    InitialUrlSearchParams.delete('glCurrency');
    InitialUrlSearchParams.delete('glCountry');
    InitialUrlSearchParams.delete('country');
  }
};
